import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import {
  Plus,
  Loader2,
  Edit,
  Trash2,
  UserCog,
  Shield,
  User,
  Phone,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export const Staff = () => {
  const { api, user, isSuperAdmin, isCenterAdmin } = useAuth();
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    surname: '',
    phone: '',
    password: '',
    role: 'STAFF',
    active: true,
  });

  const [showHoursDialog, setShowHoursDialog] = useState(false);
  const [editingHoursStaff, setEditingHoursStaff] = useState(null);
  const [hoursData, setHoursData] = useState([]);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [availWarnings, setAvailWarnings] = useState(new Set());

  const fetchAvailWarnings = useCallback(async (staffList) => {
    const withServices = staffList.filter(u => u.role === 'STAFF' && (u.serviceIds || []).length > 0);
    if (withServices.length === 0) { setAvailWarnings(new Set()); return; }
    const results = await Promise.allSettled(withServices.map(u => api.get(`/staff-availability/${u.id}`)));
    const warnings = new Set();
    withServices.forEach((u, i) => { if (results[i].status === 'rejected') warnings.add(u.id); });
    setAvailWarnings(warnings);
  }, [api]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const [res, servicesRes] = await Promise.all([
        api.get('/users'),
        api.get('/calendars'),
      ]);
      setServices((servicesRes.data || []).filter(s => s.active));
      // Filter out current user and super admins if not super admin
      const filtered = res.data.filter(u => {
        if (u.id === user.id) return false;
        if (!isSuperAdmin && u.role === 'SUPER_ADMIN') return false;
        if (u.role === 'CLIENT') return false;
        return true;
      });
      setUsers(filtered);
      fetchAvailWarnings(filtered);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error(t('staff.toast.loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, user, isSuperAdmin, fetchAvailWarnings]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenDialog = (userItem = null) => {
    if (userItem) {
      setEditingUser(userItem);
      setFormData({
        email: userItem.email,
        fullName: userItem.fullName,
        surname: '',
        phone: '',
        password: '',
        role: userItem.role,
        active: userItem.active,
        serviceIds: userItem.serviceIds || [],
        allowedSections: userItem.allowedSections || ['planner', 'clients', 'requests', 'waitlist'],
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        fullName: '',
        surname: '',
        phone: '',
        password: '',
        role: 'STAFF',
        active: true,
        serviceIds: [],
        allowedSections: ['planner', 'clients', 'requests', 'waitlist'],
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.email || !formData.fullName) {
      toast.error(t('staff.toast.emailNameRequired'));
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error(t('staff.toast.passwordRequired'));
      return;
    }
    const _norm = (v) => (v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().replace(/\s+/g, ' ').toLowerCase();
    const lookupExistingClient = async () => {
      const fn = _norm(formData.fullName);
      const sn = _norm(formData.surname);
      const combined = _norm(((formData.fullName || '') + ' ' + (formData.surname || '')).trim());
      if (!combined) return null;
      try {
        const resp = await api.get('/clients', { params: { search: formData.fullName } });
        const list = Array.isArray(resp.data) ? resp.data : [];
        if (fn && sn) {
          const precise = list.find(c => {
            const cFn = _norm(c.fullName);
            const cSn = _norm(c.surname);
            return cSn ? (cFn === fn && cSn === sn) : (cFn === combined);
          });
          if (precise) return precise;
        }
        return list.find(c => _norm(c.fullName) === combined) || null;
      } catch {
        return null;
      }
    };

    let existingClient = null;
    if (!editingUser && formData.role === 'CLIENT') {
      existingClient = await lookupExistingClient();
      if (existingClient?.userId) {
        toast.error(t('staff.toast.clientAlreadyHasAccess'));
        return;
      }
    }




    setFormLoading(true);
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, {
          email: formData.email,
          fullName: formData.fullName,
          role: formData.role,
          centerId: editingUser.centerId,
          active: formData.active,
          language: editingUser.language,
          serviceIds: formData.role === 'STAFF' ? (formData.serviceIds || []) : [],
          allowedSections: formData.role === 'STAFF' ? (formData.allowedSections || ['planner', 'clients', 'requests', 'waitlist']) : [],
        });
        toast.success(t('staff.userUpdated') || 'User updated');
      } else {
        // Create user
        const newUserRes = await api.post('/users', {
          email: formData.email,
          fullName: formData.fullName,
          password: formData.password,
          role: formData.role,
          centerId: user.centerId || '',
          language: 'es',
          active: formData.active,
          serviceIds: formData.role === 'STAFF' ? (formData.serviceIds || []) : [],
          allowedSections: formData.role === 'STAFF' ? (formData.allowedSections || ['planner', 'clients', 'requests', 'waitlist']) : [],
        });

        // If CLIENT role, also create linked Client record
        if (formData.role === 'CLIENT') {
          if (existingClient && existingClient.id) {
            // Reutiliza client existente: vincula userId y actualiza datos básicos si estaban vacíos
            await api.put('/clients/' + existingClient.id, {
              fullName: existingClient.fullName || formData.fullName,
              surname: (existingClient.surname ?? formData.surname ?? ''),
              phone: (formData.phone ?? existingClient.phone ?? ''),
              email: (existingClient.email ?? formData.email ?? ''),
              centerId: existingClient.centerId || (user.centerId || ''),
              userId: newUserRes.data.id,
              active: (existingClient.active ?? true),
            });
          } else {
            // Crea un client nuevo vinculado
            if (existingClient && existingClient.id) {
          await api.put('/clients/' + existingClient.id, {
            fullName: existingClient.fullName || combinedName || formData.fullName,
            surname: (existingClient.surname ?? formData.surname ?? ''),
            phone: (formData.phone ?? existingClient.phone ?? ''),
            email: (existingClient.email ?? formData.email ?? ''),
            centerId: existingClient.centerId || (user.centerId || ''),
            userId: newUserRes.data.id,
            active: (existingClient.active ?? true),
          });
        } else {
          await api.post('/clients', {
              fullName: formData.fullName,
              surname: formData.surname || '',
              phone: formData.phone || '',
              email: formData.email,
              centerId: user.centerId || '',
              userId: newUserRes.data.id,
              active: true,
            });
        }
}
        }
        
        toast.success(t('staff.userCreated') || 'User created');
      }
      setShowDialog(false);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || t('staff.toast.saveError');
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (userItem) => {
    if (!window.confirm(t('staff.toast.confirmDelete', { name: userItem.fullName }))) return;

    try {
      await api.delete(`/users/${userItem.id}`);
      toast.success(t('staff.toast.deleted'));
      fetchUsers();
    } catch (error) {
      toast.error(t('staff.toast.deleteError'));
    }
  };

  const openHoursDialog = async (staffMember) => {
    setEditingHoursStaff(staffMember);
    setHoursLoading(true);
    setShowHoursDialog(true);
    try {
      const res = await api.get(`/staff-availability/${staffMember.id}`);
      const existing = res.data.weeklySchedule || [];
      setHoursData(Array.from({ length: 7 }, (_, i) => {
        const found = existing.find(d => d.dayOfWeek === i);
        return found || { dayOfWeek: i, isWorking: i < 5, timeSlots: [] };
      }));
    } catch {
      setHoursData(Array.from({ length: 7 }, (_, i) => ({ dayOfWeek: i, isWorking: i < 5, timeSlots: [] })));
    } finally {
      setHoursLoading(false);
    }
  };

  const handleSaveHours = async () => {
    setHoursLoading(true);
    try {
      await api.put(`/staff-availability/${editingHoursStaff.id}`, { weeklySchedule: hoursData });
      toast.success(t('success.staff.scheduleSaved'));
      setShowHoursDialog(false);
      fetchAvailWarnings(users);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar el horario');
    } finally {
      setHoursLoading(false);
    }
  };

  const copyDayToAll = (fromDow) => {
    const sourceSlots = (hoursData.find(d => d.dayOfWeek === fromDow)?.timeSlots || []).map(s => ({ ...s }));
    setHoursData(prev => prev.map(d =>
      d.isWorking && d.dayOfWeek !== fromDow ? { ...d, timeSlots: sourceSlots.map(s => ({ ...s })) } : d
    ));
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SUPER_ADMIN': return <Shield className="w-4 h-4 text-yellow-400" />;
      case 'CENTER_ADMIN': return <UserCog className="w-4 h-4 text-blue-400" />;
      case 'CLIENT': return <User className="w-4 h-4 text-green-400" />;
      default: return <UserCog className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getRoles = () => {
    if (isSuperAdmin) {
      return ['SUPER_ADMIN', 'CENTER_ADMIN', 'STAFF'];
    }
    // CENTER_ADMIN can create STAFF and CLIENT
    return ['CENTER_ADMIN', 'STAFF'];
  };

  return (
    <div className="p-4 md:p-6 h-full overflow-y-auto" data-testid="staff-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight uppercase">
          {t('staff.title')}
        </h1>
        
        <Button
          className="btn-primary"
          onClick={() => handleOpenDialog()}
          data-testid="add-staff-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('staff.addStaff')}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          {t('staff.noUsers')}
        </div>
      ) : (
        <div className="grid gap-3">
          {users.map((userItem) => (
            <div
              key={userItem.id}
              className={`flex items-center gap-4 p-4 bg-card border rounded-sm ${
                userItem.active ? 'border-zinc-800' : 'border-zinc-900 opacity-50'
              }`}
              data-testid={`staff-row-${userItem.id}`}
            >
              <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                {userItem.fullName?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{userItem.fullName}</span>
                  {getRoleIcon(userItem.role)}
                </div>
                <div className="text-sm text-zinc-500">{userItem.email}</div>
              </div>

              <div className="flex items-center gap-2">
                {userItem.role === 'STAFF' && availWarnings.has(userItem.id) && (
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" title="Sin horario configurado" />
                )}
                <span className={`text-xs px-2 py-1 rounded ${
                  userItem.role === 'SUPER_ADMIN' ? 'bg-yellow-400/10 text-yellow-400' :
                  userItem.role === 'CENTER_ADMIN' ? 'bg-blue-400/10 text-blue-400' :
                  userItem.role === 'CLIENT' ? 'bg-green-400/10 text-green-400' :
                  'bg-zinc-800 text-zinc-400'
                }`}>
                  {t(`staff.roles.${
                    userItem.role === 'SUPER_ADMIN' ? 'superAdmin' :
                    userItem.role === 'CENTER_ADMIN' ? 'centerAdmin' :
                    userItem.role === 'CLIENT' ? 'client' :
                    'staff'
                  }`)}
                </span>

                {userItem.role === 'STAFF' && isCenterAdmin && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openHoursDialog(userItem)}
                    title="Configurar horario"
                    data-testid={`hours-staff-${userItem.id}`}
                  >
                    <Clock className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenDialog(userItem)}
                  data-testid={`edit-staff-${userItem.id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-400 hover:text-red-300"
                  onClick={() => handleDelete(userItem)}
                  data-testid={`delete-staff-${userItem.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {editingUser ? t('common.edit') : t('staff.addStaff')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('staff.role')}</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData(f => ({ ...f, role: value }))}
              >
                <SelectTrigger data-testid="staff-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getRoles().map((role) => (
                    <SelectItem key={role} value={role}>
                      {t(`staff.roles.${
                        role === 'SUPER_ADMIN' ? 'superAdmin' : 
                        role === 'CENTER_ADMIN' ? 'centerAdmin' : 
                        role === 'CLIENT' ? 'client' :
                        'staff'
                      }`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('clients.fullName')} *</Label>
              <Input
                value={formData.fullName}
                onChange={(e) => setFormData(f => ({ ...f, fullName: e.target.value }))}
                className="input-tactical"
                placeholder="John"
                data-testid="staff-name-input"
              />
            </div>

            {/* Show surname and phone for CLIENT role */}
            {formData.role === 'CLIENT' && !editingUser && (
              <>
                <div className="space-y-2">
                  <Label>{t('clients.surname') || 'Surname'}</Label>
                  <Input
                    value={formData.surname}
                    onChange={(e) => setFormData(f => ({ ...f, surname: e.target.value }))}
                    className="input-tactical"
                    placeholder="Doe"
                    data-testid="staff-surname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('clients.phone')}</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
                    className="input-tactical"
                    placeholder="+34 612 345 678"
                    data-testid="staff-phone-input"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>{t('auth.email')} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                className="input-tactical"
                placeholder="user@example.com"
                data-testid="staff-email-input"
              />
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label>{t('auth.password')} *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(f => ({ ...f, password: e.target.value }))}
                  className="input-tactical"
                  placeholder="Min 6 characters"
                  data-testid="staff-password-input"
                />
              </div>
            )}

            {formData.role === 'CLIENT' && !editingUser && (
              <div className="text-xs text-zinc-500 p-2 bg-zinc-900/50 rounded">
                {t('staff.clientNote') || 'A Client profile will be automatically created and linked to this user account.'}
              </div>
            )}

            {formData.role === 'STAFF' && (
              <div className="space-y-2">
                <Label>Acceso al menú</Label>
                <div className="space-y-2 rounded-sm border border-zinc-800 p-3">
                  {[
                    { key: 'planner',  label: 'Planificador' },
                    { key: 'clients',  label: 'Clientes' },
                    { key: 'requests', label: 'Solicitudes' },
                    { key: 'waitlist', label: 'Lista de espera' },
                    { key: 'whatsapp', label: 'WhatsApp' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={(formData.allowedSections ?? ['planner','clients','requests','waitlist']).includes(key)}
                        onChange={(e) => {
                          setFormData(f => {
                            const current = f.allowedSections ?? ['planner','clients','requests','waitlist'];
                            return {
                              ...f,
                              allowedSections: e.target.checked
                                ? [...current, key]
                                : current.filter(k => k !== key),
                            };
                          });
                        }}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {formData.role === 'STAFF' && (
              <div className="space-y-2">
                <Label>Servicios que puede impartir</Label>
                <div className="space-y-2 rounded-sm border border-zinc-800 p-3 max-h-48 overflow-y-auto" data-testid="staff-services-list">
                  {services.length === 0 ? (
                    <div className="text-sm text-zinc-500">No hay servicios activos.</div>
                  ) : services.map((service) => (
                    <label key={service.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={(formData.serviceIds || []).includes(service.id)}
                        onChange={(e) => {
                          setFormData(f => {
                            const current = f.serviceIds || [];
                            return {
                              ...f,
                              serviceIds: e.target.checked
                                ? [...current, service.id]
                                : current.filter(id => id !== service.id),
                            };
                          });
                        }}
                        data-testid={`staff-service-${service.id}`}
                      />
                      <span>{service.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}


            <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm">
              <Label>{t('common.active')}</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData(f => ({ ...f, active: checked }))}
                data-testid="staff-active-switch"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                className="btn-primary"
                onClick={handleSave}
                disabled={formLoading}
                data-testid="save-staff-btn"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Working Hours Dialog */}
      <Dialog open={showHoursDialog} onOpenChange={setShowHoursDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              Horario — {editingHoursStaff?.fullName}
            </DialogTitle>
          </DialogHeader>

          {hoursLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
            </div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {hoursData.map((day) => (
                <div key={day.dayOfWeek} className="border border-zinc-800 rounded-sm p-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={day.isWorking}
                      onCheckedChange={(checked) => setHoursData(prev =>
                        prev.map(d => d.dayOfWeek === day.dayOfWeek ? { ...d, isWorking: checked } : d)
                      )}
                    />
                    <span className="font-medium text-sm w-24">{DAY_NAMES[day.dayOfWeek]}</span>
                    {day.isWorking && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-zinc-500 ml-auto h-7 px-2"
                        onClick={() => copyDayToAll(day.dayOfWeek)}
                      >
                        Copiar a todos
                      </Button>
                    )}
                  </div>

                  {day.isWorking && (
                    <div className="space-y-2 pl-9">
                      {day.timeSlots.map((slot, si) => (
                        <div key={si} className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={slot.start}
                            onChange={(e) => setHoursData(prev => prev.map(d =>
                              d.dayOfWeek === day.dayOfWeek
                                ? { ...d, timeSlots: d.timeSlots.map((s, j) => j === si ? { ...s, start: e.target.value } : s) }
                                : d
                            ))}
                            className="input-tactical w-28 text-sm"
                          />
                          <span className="text-zinc-500 text-sm">–</span>
                          <Input
                            type="time"
                            value={slot.end}
                            onChange={(e) => setHoursData(prev => prev.map(d =>
                              d.dayOfWeek === day.dayOfWeek
                                ? { ...d, timeSlots: d.timeSlots.map((s, j) => j === si ? { ...s, end: e.target.value } : s) }
                                : d
                            ))}
                            className="input-tactical w-28 text-sm"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-7 h-7 text-red-400 hover:text-red-300"
                            onClick={() => setHoursData(prev => prev.map(d =>
                              d.dayOfWeek === day.dayOfWeek
                                ? { ...d, timeSlots: d.timeSlots.filter((_, j) => j !== si) }
                                : d
                            ))}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-zinc-400 pl-0 h-7"
                        onClick={() => setHoursData(prev => prev.map(d =>
                          d.dayOfWeek === day.dayOfWeek
                            ? { ...d, timeSlots: [...d.timeSlots, { start: '09:00', end: '20:00' }] }
                            : d
                        ))}
                      >
                        + Añadir franja
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowHoursDialog(false)}>
              Cancelar
            </Button>
            <Button
              className="btn-primary"
              onClick={handleSaveHours}
              disabled={hoursLoading}
            >
              {hoursLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar horario'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
